import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  Building2,
  Briefcase,
  GraduationCap,
  Mail,
  Volume2,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Coffee,
  Clock,
  Users,
  MessageSquare,
  Award,
  Lightbulb,
  FileText,
  Phone,
  Cake
} from 'lucide-react';

// VOCAB by category & level
const VOCAB = {
  application: {
    label: 'Application & Interview',
    icon: <FileText className="w-4 h-4" />,
    items: [
      { de: 'der Lebenslauf', en: 'CV / résumé', level: 'A2' },
      { de: 'die Bewerbung', en: 'application', level: 'A2' },
      { de: 'das Anschreiben', en: 'cover letter', level: 'B1' },
      { de: 'das Motivationsschreiben', en: 'motivational letter', level: 'B1' },
      { de: 'die Initiativbewerbung', en: 'speculative application (no posted ad)', level: 'B2' },
      { de: 'der Werdegang', en: 'career path (on a CV)', level: 'B2' },
      { de: 'das Bewerbungsverfahren', en: 'application process', level: 'B2' },
      { de: 'das Assessment Center', en: 'assessment center — where employers test you against other candidates', level: 'B2' },
      { de: 'die Sprachkenntnisse', en: 'language skills', level: 'B1' },
      { de: 'Frau Müller', en: 'Mrs. Müller', level: 'A1' },
      { de: 'Herr Müller', en: 'Mr. Müller', level: 'A1' },
    ]
  },
  qualifications: {
    label: 'Qualifications & Skills',
    icon: <Award className="w-4 h-4" />,
    items: [
      { de: 'die Qualifikationen', en: 'qualifications', level: 'B1' },
      { de: 'die Fähigkeit', en: 'skill / ability', level: 'A2' },
      { de: 'die Kompetenz', en: 'competence', level: 'B1' },
      { de: 'die Stärke / die Stärken', en: 'strength / strengths', level: 'A2' },
      { de: 'die Schwäche / die Schwächen', en: 'weakness / weaknesses', level: 'A2' },
      { de: 'die Herausforderung', en: 'challenge', level: 'B1' },
      { de: 'verfügen über (+Akk)', en: 'to possess / to have at one\'s disposal', level: 'B2' },
      { de: 'resilient', en: 'resilient', level: 'B2' },
      { de: 'sich mit etwas identifizieren', en: 'to identify with something', level: 'B2' },
      { de: 'verantwortlich sein', en: 'to be responsible', level: 'B1' },
      { de: 'crossfunktional', en: 'cross-functional', level: 'C1' },
    ]
  },
  office: {
    label: 'The Office',
    icon: <Building2 className="w-4 h-4" />,
    items: [
      { de: 'der Anruf', en: 'phone call', level: 'A1' },
      { de: 'der Anrufbeantworter', en: 'voicemail / answering machine', level: 'A2' },
      { de: 'die E-Mail', en: 'e-mail', level: 'A1' },
      { de: 'das Fax', en: 'fax', level: 'A1' },
      { de: 'der Computer', en: 'computer', level: 'A1' },
      { de: 'der Tacker', en: 'stapler', level: 'A1' },
      { de: 'der Einstand', en: 'welcome treats (newbie brings cake on day 1)', level: 'B1' },
      { de: 'der Ausstand', en: 'farewell treats (when you leave the company)', level: 'B1' },
    ]
  },
  interview: {
    label: 'Interview Phrases',
    icon: <MessageSquare className="w-4 h-4" />,
    items: [
      { de: 'Guten Tag', en: 'Good day', level: 'A1' },
      { de: 'Mein Name ist…', en: 'My name is…', level: 'A1' },
      { de: 'Ich bewerbe mich auf die Stelle…', en: 'I am applying for the position…', level: 'B1' },
      { de: 'In meinem Studium lernte ich…', en: 'In my studies I learned… (Präteritum)', level: 'B1' },
      { de: 'Meine Vorstellung des Gehalts', en: 'My salary expectation', level: 'B1' },
      { de: 'die Einarbeitung / das Onboarding', en: 'onboarding', level: 'B1' },
      { de: 'die Weiterbildungsmöglichkeiten', en: 'opportunities for further training', level: 'B2' },
      { de: 'Das ist eine gute Frage.', en: 'That is a good question.', level: 'A2' },
      { de: 'Können Sie das noch einmal wiederholen?', en: 'Could you repeat that one more time?', level: 'A2' },
      { de: 'Leider weiß ich nicht, was ich sagen soll.', en: 'Unfortunately, I don\'t know what to answer.', level: 'B1' },
    ]
  },
  hr: {
    label: 'HR Phrases (what they\'ll say to you)',
    icon: <Users className="w-4 h-4" />,
    items: [
      { de: 'Darf ich Ihnen etwas zu trinken anbieten?', en: 'May I offer you something to drink?', level: 'B1' },
      { de: 'Können Sie mir etwas über Ihren Werdegang erzählen?', en: 'Can you tell me about your career path?', level: 'B2' },
    ]
  }
};

const LEVELS = ['ALL', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const LEVEL_COLORS = {
  A1: 'bg-green-100 text-green-800',
  A2: 'bg-teal-100 text-teal-800',
  B1: 'bg-blue-100 text-blue-800',
  B2: 'bg-blue-100 text-blue-800',
  C1: 'bg-blue-100 text-blue-800',
  C2: 'bg-pink-100 text-pink-800',
};

const BusinessCareerPage = () => {
  const [activeCategory, setActiveCategory] = useState('application');
  const [activeLevel, setActiveLevel] = useState('ALL');
  const [playingAudio, setPlayingAudio] = useState(null);

  const speak = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'de-DE';
      u.rate = 0.9;
      u.onstart = () => setPlayingAudio(id);
      u.onend = () => setPlayingAudio(null);
      window.speechSynthesis.speak(u);
    }
  };

  const filteredItems = VOCAB[activeCategory].items.filter(
    item => activeLevel === 'ALL' || item.level === activeLevel
  );

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="business-career-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Briefcase className="w-4 h-4" />
            Career Path
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Business &amp; Office
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            Büro &amp; Geschäftswelt — climb the German corporate ladder via university, Fachhochschule, or Ausbildung.
          </p>
        </div>
      </section>

      {/* Two Paths Intro */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8">
            <p className="text-babyblue-700 text-lg leading-relaxed mb-4">
              Germany has many ways to succeed in the corporate ladder. You can get in by studying at a <strong>University</strong> or <strong>Fachhochschule</strong> (University of Applied Sciences) — or by going through the German vocational training system called <strong>Ausbildung</strong>.
            </p>
            <p className="text-babyblue-700 text-lg leading-relaxed mb-4">
              You can also work in Germany if you studied abroad. Most jobs require good spoken and written German. Whether you want to work in finance, marketing, or HR — good German is a must.
            </p>
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg mt-6">
              <p className="font-semibold text-amber-900 mb-1">💡 Fun fact</p>
              <p className="text-amber-800">You can reach the exact same corporate level without going to university.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ausbildung Path */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-8 text-center">
            The Ausbildung Path
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-50 border-2 border-blue-300 rounded-2xl p-6">
              <GraduationCap className="w-10 h-10 text-blue-700 mb-3" />
              <h3 className="font-bold text-xl text-blue-900 mb-2">Ausbildung + Fachwirt</h3>
              <p className="text-blue-900 text-lg font-semibold mb-1">≈ Bachelor's Degree</p>
              <p className="text-blue-800 text-sm">Reach the same level as a university bachelor's — without university debt.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-pink-50 border-2 border-blue-300 rounded-2xl p-6">
              <Award className="w-10 h-10 text-blue-700 mb-3" />
              <h3 className="font-bold text-xl text-blue-900 mb-2">Ausbildung + Betriebswirt</h3>
              <p className="text-blue-900 text-lg font-semibold mb-1">≈ Master's Degree</p>
              <p className="text-blue-800 text-sm">Top-tier corporate qualification — fully paid by your employer.</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 space-y-3">
            <p className="text-babyblue-700"><strong>Ausbildung</strong> is a 2–3 year program combining theory and practical vocational training.</p>
            <p className="text-babyblue-700"><strong>Fachwirt</strong> and <strong>Betriebswirt</strong> are continuation programs for further education.</p>
            <p className="text-babyblue-700">✨ Both the <strong>Ausbildung</strong> and the <strong>Fach-/Betriebswirt</strong> are <strong>paid by an employer</strong>.</p>
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg mt-4">
              <p className="text-green-900">
                <strong>Didn't go to high school?</strong> Who cares? As long as you have your <strong>Realschulabschluss</strong> (10th-grade graduation), you can start your corporate journey without academic stress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Communication */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-6 text-center">Communication</h2>
          <p className="text-babyblue-700 text-lg leading-relaxed text-center mb-4">
            Communication is <strong>key</strong> — especially if your diploma is from abroad. Most German employers want German, and even if it's not officially required, you'll miss out on:
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {['Networking', 'Lunch breaks', 'The latest office gossip 👀'].map((item, i) => (
              <div key={i} className="bg-babyblue-50 border border-babyblue-200 rounded-xl p-4 text-center">
                <p className="font-semibold text-babyblue-800">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-babyblue-600 text-center mt-4 italic">Not required — but they make all the difference.</p>
        </div>
      </section>

      {/* Job Interview Guide */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">Guide for a Job Interview</h2>
          <p className="text-babyblue-600 text-center mb-8">Vorstellungsgespräch — what to actually say</p>

          {/* Grammar Tip */}
          <div className="bg-amber-50 border-l-4 border-amber-400 p-5 rounded-r-xl mb-8 flex items-start gap-3" data-testid="interview-grammar-tip">
            <Lightbulb className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <p className="font-bold text-amber-900 mb-1">Grammar Tip</p>
              <p className="text-amber-800">
                You'll talk about past events — some still relevant, some completed. So master the{' '}
                <Link to="/grammar?level=B1" className="font-semibold underline">Präteritum</Link> and{' '}
                <Link to="/grammar?level=B2" className="font-semibold underline">Perfekt</Link> tenses.
              </p>
            </div>
          </div>

          {/* You vs HR phrases */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
              <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" /> You say…
              </h3>
              <div className="space-y-2">
                {VOCAB.interview.items.map((phrase, i) => (
                  <button
                    key={i}
                    onClick={() => speak(phrase.de, `you-${i}`)}
                    className="w-full text-left bg-babyblue-50 hover:bg-babyblue-100 p-3 rounded-lg border border-babyblue-200 transition-all group flex items-center justify-between"
                  >
                    <div>
                      <p className="font-semibold text-babyblue-800">{phrase.de}</p>
                      <p className="text-sm text-babyblue-600">{phrase.en}</p>
                    </div>
                    <Volume2 className={`w-4 h-4 ${playingAudio === `you-${i}` ? 'text-primary animate-pulse' : 'text-babyblue-400'}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
              <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" /> HR says…
              </h3>
              <div className="space-y-2">
                {VOCAB.hr.items.map((phrase, i) => (
                  <button
                    key={i}
                    onClick={() => speak(phrase.de, `hr-${i}`)}
                    className="w-full text-left bg-babyblue-50 hover:bg-babyblue-100 p-3 rounded-lg border border-babyblue-200 transition-all flex items-center justify-between"
                  >
                    <div>
                      <p className="font-semibold text-babyblue-800">{phrase.de}</p>
                      <p className="text-sm text-babyblue-600">{phrase.en}</p>
                    </div>
                    <Volume2 className={`w-4 h-4 ${playingAudio === `hr-${i}` ? 'text-primary animate-pulse' : 'text-babyblue-400'}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pro Tip */}
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-pink-300 rounded-2xl p-6 mt-6">
            <p className="font-bold text-pink-900 mb-2">🐠 Pro tip — when you don't understand the question:</p>
            <p className="text-pink-900">
              Do <strong>not freeze like a goldfish in ice water</strong>. Instead, calmly say:
            </p>
            <ul className="mt-3 space-y-2 text-pink-900">
              <li>• <strong>"Können Sie das noch einmal wiederholen?"</strong> — Could you repeat that one more time?</li>
              <li>• Or honestly: <strong>"Leider weiß ich nicht, was ich sagen soll."</strong> — Unfortunately I don't know what to answer.</li>
            </ul>
          </div>

          {/* Culture: Du vs Sie */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 mt-6">
            <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-3 flex items-center gap-2">
              <Coffee className="w-5 h-5 text-primary" /> Culture: Du or Sie?
            </h3>
            <p className="text-babyblue-700 leading-relaxed">
              Modern German companies increasingly go <strong>"per Du"</strong> — colleagues address each other informally as <em>du</em>. This often happens even before you're hired, so phone calls and interviews can feel casual.
            </p>
            <p className="text-babyblue-700 leading-relaxed mt-3">
              <strong>Safer strategy:</strong> Start with <em>Sie</em> and follow the company's lead. If they keep using <em>Sie</em>, stick with <em>Sie</em>. If they switch to <em>du</em>, switch with them.
            </p>
          </div>
        </div>
      </section>

      {/* VOCAB — Interactive by Category + Level */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50 to-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Business Vocabulary
          </h2>
          <p className="text-babyblue-600 text-center mb-8">
            Filter by category and CEFR level. Click any word to hear it.
          </p>

          {/* Category tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {Object.entries(VOCAB).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeCategory === key
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white text-babyblue-700 border border-babyblue-200 hover:bg-babyblue-50'
                }`}
                data-testid={`vocab-category-${key}`}
              >
                {cat.icon}
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Level filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {LEVELS.map((lvl) => (
              <button
                key={lvl}
                onClick={() => setActiveLevel(lvl)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  activeLevel === lvl
                    ? 'bg-babyblue-800 text-white'
                    : 'bg-white text-babyblue-700 border border-babyblue-200 hover:bg-babyblue-100'
                }`}
                data-testid={`vocab-level-${lvl.toLowerCase()}`}
              >
                {lvl}
              </button>
            ))}
          </div>

          {/* Vocab grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vocab-grid">
            {filteredItems.length === 0 ? (
              <div className="col-span-full text-center text-babyblue-500 italic py-8">
                No items at this level for this category.
              </div>
            ) : filteredItems.map((word, i) => (
              <button
                key={i}
                onClick={() => speak(word.de, `voc-${i}`)}
                className="bg-white rounded-xl p-4 border border-babyblue-200 hover:border-primary hover:shadow-md transition-all text-left group"
                data-testid={`vocab-word-${i}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-bold text-babyblue-800">{word.de}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${LEVEL_COLORS[word.level]}`}>
                    {word.level}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-babyblue-600">{word.en}</p>
                  <Volume2 className={`w-4 h-4 flex-shrink-0 ${playingAudio === `voc-${i}` ? 'text-primary animate-pulse' : 'text-babyblue-400 opacity-0 group-hover:opacity-100'}`} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Survival Guide */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Got the Job? Now Survive.
          </h2>
          <p className="text-babyblue-600 text-center mb-8">
            The German work environment — language &amp; culture mastery.
          </p>

          {/* Der Einstand */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-200 rounded-xl">
                <Cake className="w-7 h-7 text-amber-700" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-amber-900 mb-2">Der Einstand</h3>
                <p className="text-amber-900 leading-relaxed mb-3">
                  One of the most important unwritten German workplace rules. <strong>Newbies bring baked pastries</strong> — muffins, cupcakes, a Kuchen — on day one.
                </p>
                <p className="text-amber-900 italic font-semibold">
                  „Der erste Eindruck zählt." — The first impression counts. Not the second, not the third. The <strong>first</strong>.
                </p>
                <p className="text-amber-800 mt-3 text-sm">
                  Same rule on your last day — that's called <strong>der Ausstand</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* First Week */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 mb-6">
            <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" /> Your First Week — Cheat Sheet
            </h3>
            <ul className="space-y-3 text-babyblue-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Use <strong>Sie</strong> as your baseline with everyone — unless they explicitly insist on <em>du</em>.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Be <strong>organized</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>BE ON TIME.</strong> In Germany, we say being on time <em>is already too late</em> — show up <strong>5 minutes early</strong>.</span>
              </li>
            </ul>
          </div>

          {/* Email Etiquette */}
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
            <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" /> How to Write an E-mail
            </h3>
            <p className="text-babyblue-700 mb-4">
              You start with an <strong>Anrede</strong> (opener):
            </p>
            <div className="bg-babyblue-50 border border-babyblue-200 rounded-xl p-4 mb-4">
              <p className="font-mono text-babyblue-900 font-semibold">Sehr geehrte Damen und Herren,</p>
              <p className="text-sm text-babyblue-600 mt-1">— "Dear Sir or Madam,"</p>
            </div>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg mb-4">
              <p className="font-bold text-red-900 mb-1">⚠️ Critical rule</p>
              <p className="text-red-800">
                After the Anrede (which ends with a comma), the <strong>first letter of the next line is lower-case</strong>.
              </p>
            </div>
            <div className="bg-babyblue-50 border border-babyblue-200 rounded-xl p-4 font-mono text-sm text-babyblue-900 leading-relaxed">
              <p className="font-semibold">Sehr geehrte Damen und Herren,</p>
              <p>hiermit bewerbe ich mich auf die Stelle als…</p>
              <p className="text-babyblue-500 italic text-xs mt-2 not-italic font-sans">↑ Note the lowercase "h" after the comma.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Explore Other Career Paths</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '🏥', title: 'Healthcare', path: '/careers/healthcare' },
              { icon: '🍽️', title: 'Hospitality', path: '/careers/hospitality' },
              { icon: '🚚', title: 'Logistics', path: '/careers/logistics' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="bg-white rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessCareerPage;
